// Tabs — controlled segmented tab row. Chrome is shared with TabNav in tab-nav.css.
// state. Optional per-tab icon (Bootstrap/FA class) and count badge. a11y (Phase 17): roving tabindex
// + arrow/Home/End keyboard navigation (WCAG tab pattern, automatic activation); pass `controls` to
// associate a tab with its panel id.
import { useRef, type CSSProperties } from 'react';
import './Tabs.css';

export interface TabDef {
  id: string;
  label: string;
  icon?: string; // e.g. 'bi bi-people'
  count?: number;
  controls?: string; // id of the panel this tab controls (for aria-controls)
  disabled?: boolean;
  /** Optional fill matching a Select option swatch (deal pipeline stages). */
  swatch?: string;
  swatchFg?: string;
}

export interface TabsProps {
  tabs: TabDef[];
  active: string;
  onChange: (id: string) => void;
  /** Optional accessible name for the tablist. */
  ariaLabel?: string;
  /** Extra classes on the track (e.g. `quralyst-tabs--flush` in a toolbar). */
  className?: string;
}

export default function Tabs({ tabs, active, onChange, ariaLabel, className }: TabsProps) {
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const focusTab = (index: number) => {
    const tab = tabs[index];
    if (!tab || tab.disabled) return;
    onChange(tab.id);
    btnRefs.current[index]?.focus();
  };

  const nextEnabled = (from: number, step: number) => {
    for (let n = 1; n <= tabs.length; n += 1) {
      const i = (from + step * n + tabs.length * n) % tabs.length;
      if (!tabs[i]?.disabled) return i;
    }
    return from;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = nextEnabled(index, 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = nextEnabled(index, -1);
    else if (e.key === 'Home') next = tabs.findIndex((t) => !t.disabled);
    else if (e.key === 'End') {
      for (let i = tabs.length - 1; i >= 0; i -= 1) {
        if (!tabs[i]?.disabled) {
          next = i;
          break;
        }
      }
    } else return;
    if (next < 0) return;
    e.preventDefault();
    focusTab(next);
  };

  return (
    <div
      className={['quralyst-tabs', className].filter(Boolean).join(' ')}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab, i) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              btnRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={tab.controls}
            tabIndex={isActive ? 0 : -1}
            disabled={tab.disabled}
            className={`btn-unstyled quralyst-tab${isActive ? ' active' : ''}${
              tab.swatch ? ' quralyst-tab--swatch' : ''
            }`}
            style={
              tab.swatch
                ? ({
                    '--deal-tab-swatch': tab.swatch,
                    '--deal-tab-fg': tab.swatchFg || 'var(--brand-navy)',
                  } as CSSProperties)
                : undefined
            }
            onClick={() => {
              if (!tab.disabled) onChange(tab.id);
            }}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {tab.icon && <i className={tab.icon} aria-hidden="true" />}
            <span>{tab.label}</span>
            {tab.count !== undefined && <span className="quralyst-tab-count">{tab.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
